import React, { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import styled from 'styled-components';
import { useHorizontalScrollArrows } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-horizontal-scroll-arrows';
import { useTableArrowsPortal } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-table-arrows-portal';

type ScrollSide = 'left' | 'right';

interface HorizontalScrollFrameProps {
  children: (
    scrollRef: React.RefObject<HTMLDivElement>,
    headerRef: React.RefObject<HTMLTableSectionElement>,
  ) => ReactNode;
}

/**
 * Wraps a horizontally scrollable element and, while the table is hovered,
 * portals a full-size overlay (`inset: 0`) into the nearest ancestor
 * matching `.table-arrows-portal` (skipped entirely if none exists). The
 * overlay holds a left/right "arrow zone" strip, each positioned at the
 * table's edge and sized to the table's own visible height, so its arrow
 * button stays vertically centered relative to the table via flexbox rather
 * than a manually computed offset. The left zone starts below the frozen
 * header row.
 */
export function HorizontalScrollFrame({
  children,
}: HorizontalScrollFrameProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const {
    canScrollLeft,
    canScrollRight,
    scrollStep,
    startAutoScroll,
    stopAutoScroll,
  } = useHorizontalScrollArrows(scrollRef);
  const geometry = useTableArrowsPortal(frameRef, headerRef, isHovering);

  const showLeft = canScrollLeft && Boolean(geometry?.left);
  const showRight = canScrollRight && Boolean(geometry?.right);

  return (
    <ScrollFrame
      ref={frameRef}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {children(scrollRef, headerRef)}
      {geometry &&
        (showLeft || showRight) &&
        createPortal(
          <ArrowsOverlay>
            {showLeft && geometry.left && (
              <ArrowZone
                side="left"
                rect={geometry.left}
                onClick={() => scrollStep(`left`)}
                onScrollHoldEnd={stopAutoScroll}
                onScrollHoldStart={() => startAutoScroll(`left`)}
              />
            )}
            {showRight && geometry.right && (
              <ArrowZone
                side="right"
                rect={geometry.right}
                onClick={() => scrollStep(`right`)}
                onScrollHoldEnd={stopAutoScroll}
                onScrollHoldStart={() => startAutoScroll(`right`)}
              />
            )}
          </ArrowsOverlay>,
          geometry.portalNode,
        )}
    </ScrollFrame>
  );
}

interface ArrowZoneProps {
  onClick: () => void;
  onScrollHoldEnd: () => void;
  onScrollHoldStart: () => void;
  rect: { height: number; left: number; top: number };
  side: ScrollSide;
}

function ArrowZone({
  onClick,
  onScrollHoldEnd,
  onScrollHoldStart,
  rect,
  side,
}: ArrowZoneProps) {
  return (
    <ArrowZoneStrip
      $side={side}
      role="button"
      tabIndex={0}
      aria-label={`Scroll table ${side}`}
      style={{ height: `100%`, left: rect.left, top: rect.top }}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === `Enter` || event.key === ` `) {
          event.preventDefault();
          onClick();
        }
      }}
      onMouseDown={onScrollHoldStart}
      onMouseEnter={onScrollHoldStart}
      onMouseLeave={onScrollHoldEnd}
      onMouseUp={onScrollHoldEnd}
    >
      <ArrowButton>
        {side === `left` ? <FiChevronLeft /> : <FiChevronRight />}
      </ArrowButton>
    </ArrowZoneStrip>
  );
}

const ScrollFrame = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const ArrowsOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  pointer-events: none;
`;

const ArrowZoneStrip = styled.div<{ $side: ScrollSide }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.8rem;
  pointer-events: auto;
  cursor: pointer;
  background: ${({ $side }) =>
    $side === `left`
      ? `transparent`
      : `linear-gradient(to left, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0))`};
`;

const ArrowButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  color: #374151;
  font-size: 1.6rem;
  transition: background-color 150ms ease, color 150ms ease,
    box-shadow 150ms ease;

  ${ArrowZoneStrip}:hover &,
  ${ArrowZoneStrip}:focus-visible & {
    background: rgb(236, 242, 252);
    color: rgb(52, 99, 232);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;
